import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Section, StaffItem, RoleObject } from '../models/ydata.model';
import { TaskItem } from '../models/task.model';
import { BaseApiService } from './api-base.service';
import { HelperService } from './helper.service';
import { StaffDetails, ParsedStaffDetails } from '../models/staff.model';
import { getSectionType } from '../models/filter.model';

export interface RootSiblings {
  shifts: StaffItem[];
  staff: StaffItem[];
}

@Injectable({
  providedIn: 'root'
})
export class StaffService extends BaseApiService {
  private _ydata: RoleObject[] = [];
  private env: string;

  constructor(
    http: HttpClient,
    msalService: MsalService,
    private helperService: HelperService
  ) {
    super(http, msalService);
    this.env = this.helperService.getStoredValue('ecase_env', 'prod');
  }

  // Get staff details by email
  public getStaffDetails(email: string): Observable<ParsedStaffDetails> {
    return this.get<StaffDetails>(`${this.apiUrl}/ecase/${this.env}/staff-details/?email=${encodeURIComponent(email)}`).pipe(
      map(details => this.parseStaffDetails(details))
    );
  }

  // Refresh staff details
  public refreshStaffCache(): Observable<void> {
    return this.get<void>(`${this.apiUrl}/ecase/${this.env}/refresh/staff-details`);
  }

  // Parse staff details to split roles into array
  private parseStaffDetails(details: StaffDetails): ParsedStaffDetails {
    const { Role, ...rest } = details;
    return {
      ...rest,
      roles_string: Role || '',
      roles: Role ? Role.split(',').map(role => role.trim()).filter(role => role !== '') : []
    };
  }

  setYData(ydata: RoleObject[]) {
    this._ydata = ydata;
  }

  getSiblings(event: TaskItem): StaffItem[] {
    if (!event || !this._ydata || !event.carer_string) {
      return [];
    }

    const carerKey = parseInt(event.carer_string, 10);
    const findParentSection = (sections: (Section | StaffItem)[]): Section | null => {
      for (const item of sections) {
        if ('children' in item) {
          const section = item as Section;
          if (section.children) {
            const foundStaff = section.children.find((child: StaffItem | Section) => 'key' in child && child.key === carerKey);
            if (foundStaff) {
              return section;
            }
            const nestedResult = findParentSection(section.children);
            if (nestedResult) {
              return nestedResult;
            }
          }
        }
      }
      return null;
    };

    for (const roleObject of this._ydata) {
      const parentSection = findParentSection(roleObject.children);
      if (parentSection && parentSection.children) {
        return parentSection.children as StaffItem[];
      }
    }

    return [];
  }

  getRootSiblings(event: TaskItem): RootSiblings {
    if (!event || !this._ydata || !event.carer_string) {
      return { shifts: [], staff: [] };
    }

    const carerKey = parseInt(event.carer_string, 10);
    
    // Find the root RoleObject containing this staff member
    const findRootRoleObject = (): RoleObject | null => {
      const findStaffInSections = (sections: (Section | StaffItem)[]): boolean => {
        for (const item of sections) {
          if ('children' in item) {
            const section = item as Section;
            if (section.children && findStaffInSections(section.children)) {
              return true;
            }
          } else {
            const staffItem = item as StaffItem;
            if (staffItem.key === carerKey) {
              return true;
            }
          }
        }
        return false;
      };

      for (const roleObject of this._ydata) {
        if (findStaffInSections(roleObject.children)) {
          return roleObject;
        }
      }
      return null;
    };

    const rootRoleObject = findRootRoleObject();
    if (!rootRoleObject) {
      return { shifts: [], staff: [] };
    }

    // Extract all staff from SHIFTS and STAFF sections within this root
    const result: RootSiblings = { shifts: [], staff: [] };
    
    const extractStaffFromSections = (sections: (Section | StaffItem)[]): void => {
      for (const item of sections) {
        if ('children' in item) {
          const section = item as Section;
          const sectionType = getSectionType(section.key);
          
          if (sectionType === 'shifts' || sectionType === 'staff') {
            // This is a SHIFTS or STAFF section - extract all staff
            if (section.children) {
              const staffInSection = section.children.filter(child => !('children' in child)) as StaffItem[];
              if (sectionType === 'shifts') {
                result.shifts.push(...staffInSection);
              } else {
                result.staff.push(...staffInSection);
              }
            }
          } else if (section.children) {
            // Recurse into nested sections
            extractStaffFromSections(section.children);
          }
        }
      }
    };

    extractStaffFromSections(rootRoleObject.children);
    return result;
  }

  getStaffLabel(carer_string: string): string | undefined {
    if (!this._ydata || !carer_string) {
      return undefined;
    }

    const carerKey = parseInt(carer_string, 10);
    
    const findStaffItem = (items: (Section | StaffItem)[]): string | undefined => {
      for (const item of items) {
        if ('children' in item) {
          // This is a Section
          const section = item as Section;
          if (section.children) {
            const result = findStaffItem(section.children);
            if (result) {
              return result;
            }
          }
        } else {
          // This is a StaffItem
          const staffItem = item as StaffItem;
          if (staffItem.key === carerKey) {
            return staffItem.label;
          }
        }
      }
      return undefined;
    };

    for (const roleObject of this._ydata) {
      const result = findStaffItem(roleObject.children);
      if (result) {
        return result;
      }
    }

    return undefined;
  }

  /**
   * Gets all staff members from the ydata hierarchy
   * @returns Array of all StaffItem objects
   */
  getAllStaff(): StaffItem[] {
    if (!this._ydata) {
      return [];
    }

    const allStaff: StaffItem[] = [];
    
    const extractStaff = (items: (Section | StaffItem)[]): void => {
      for (const item of items) {
        if ('children' in item) {
          // This is a Section - recurse into children
          const section = item as Section;
          if (section.children) {
            extractStaff(section.children);
          }
        } else {
          // This is a StaffItem - add to our list
          const staffItem = item as StaffItem;
          allStaff.push(staffItem);
        }
      }
    };

    for (const roleObject of this._ydata) {
      extractStaff(roleObject.children);
    }

    return allStaff;
  }
}
